/**
 * Illustrations for the eight components in the "Inside a Computer
 * System" lab.
 *
 * Drawn as inline SVG rather than loaded as photographs: nothing to
 * fetch, nothing to licence, they stay sharp at any size, and the page
 * needs no image host - which matters because the production Content
 * Security Policy only allows images from this origin and data URIs.
 *
 * Deliberately diagrammatic rather than realistic. The point is that a
 * learner can recognise the shape of the thing when they open a case,
 * so each one keeps the silhouette and the one or two details that
 * actually identify it: the notch on a RAM stick, the pins under a CPU,
 * the RJ45 jack on a network card.
 */

const BOARD = '#2f7d4f';
const BOARD_DARK = '#215c39';
const CHIP = '#2d2a45';
const CHIP_LIGHT = '#4a4568';
const GOLD = '#e0a413';
const METAL = '#b9b6c9';
const METAL_DARK = '#8e89a8';

const frame = {
  viewBox: '0 0 160 100',
  xmlns: 'http://www.w3.org/2000/svg',
  role: 'img',
};

function Motherboard() {
  return (
    <svg {...frame} aria-label="A motherboard: a green circuit board with a processor socket, memory slots and expansion slots">
      <rect x="8" y="8" width="144" height="84" rx="4" fill={BOARD} stroke={CHIP} strokeWidth="3" />
      {/* traces */}
      {[22, 30, 38].map((y) => (
        <path key={y} d={`M16 ${y} H60 L68 ${y + 8} H120`} stroke={BOARD_DARK} strokeWidth="1.5" fill="none" />
      ))}
      {/* cpu socket */}
      <rect x="26" y="26" width="34" height="34" rx="2" fill={CHIP_LIGHT} stroke={CHIP} strokeWidth="2" />
      <rect x="32" y="32" width="22" height="22" rx="1" fill={METAL} />
      {/* memory slots */}
      {[74, 84, 94, 104].map((x) => (
        <rect key={x} x={x} y="18" width="5" height="44" rx="1.5" fill={CHIP} />
      ))}
      {/* expansion slot */}
      <rect x="26" y="70" width="80" height="6" rx="2" fill={CHIP} />
      {/* back ports */}
      <rect x="8" y="14" width="10" height="26" rx="2" fill={METAL_DARK} />
      {/* mounting holes */}
      {[[14, 86], [146, 14], [146, 86]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#fff9f0" stroke={CHIP} strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function Cpu() {
  return (
    <svg {...frame} aria-label="A processor: a square chip with a notched corner and rows of contact pins">
      {/* pins */}
      {Array.from({ length: 9 }, (_, i) => (
        <rect key={`t${i}`} x={46 + i * 8} y="14" width="4" height="8" rx="1" fill={GOLD} />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <rect key={`b${i}`} x={46 + i * 8} y="78" width="4" height="8" rx="1" fill={GOLD} />
      ))}
      <rect x="42" y="22" width="76" height="56" rx="3" fill={CHIP} stroke={CHIP} strokeWidth="2" />
      {/* the notch that shows which way round it goes */}
      <path d="M42 30 L50 22 H42 Z" fill="#fff9f0" />
      <rect x="56" y="34" width="48" height="32" rx="2" fill={METAL} />
      <rect x="64" y="42" width="32" height="16" rx="1" fill={METAL_DARK} />
      <text x="80" y="54" textAnchor="middle" fontSize="8" fill="#fff9f0" fontFamily="monospace">CPU</text>
    </svg>
  );
}

function Ram() {
  return (
    <svg {...frame} aria-label="A memory stick: a long green board with black chips and a notch along the gold contacts">
      <rect x="8" y="30" width="144" height="40" rx="3" fill={BOARD} stroke={CHIP} strokeWidth="3" />
      {/* chips */}
      {[18, 44, 70, 96, 122].map((x) => (
        <rect key={x} x={x} y="38" width="20" height="16" rx="1.5" fill={CHIP} />
      ))}
      {/* gold contacts with the alignment notch */}
      <rect x="10" y="62" width="60" height="8" fill={GOLD} />
      <rect x="78" y="62" width="72" height="8" fill={GOLD} />
      <rect x="70" y="60" width="8" height="12" fill="#fff9f0" stroke={CHIP} strokeWidth="1.5" />
    </svg>
  );
}

function Storage() {
  return (
    <svg {...frame} aria-label="Storage: a solid-state drive beside a hard drive showing its spinning platter">
      {/* SSD */}
      <rect x="10" y="26" width="62" height="48" rx="4" fill={CHIP_LIGHT} stroke={CHIP} strokeWidth="3" />
      <rect x="18" y="34" width="46" height="18" rx="2" fill={METAL} />
      <text x="41" y="47" textAnchor="middle" fontSize="9" fill={CHIP} fontFamily="monospace">SSD</text>
      {[20, 34, 48].map((x) => (
        <rect key={x} x={x} y="58" width="10" height="8" rx="1" fill={CHIP} />
      ))}
      {/* HDD */}
      <rect x="86" y="26" width="64" height="48" rx="4" fill={METAL} stroke={CHIP} strokeWidth="3" />
      <circle cx="118" cy="50" r="17" fill={METAL_DARK} />
      <circle cx="118" cy="50" r="5" fill="#fff9f0" stroke={CHIP} strokeWidth="1.5" />
      <path d="M140 34 L124 46" stroke={CHIP} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function Network() {
  return (
    <svg {...frame} aria-label="A network adapter: an expansion card with an Ethernet socket and gold edge connector">
      <rect x="20" y="20" width="112" height="46" rx="3" fill={BOARD} stroke={CHIP} strokeWidth="3" />
      {/* controller chip */}
      <rect x="58" y="32" width="26" height="22" rx="2" fill={CHIP} />
      {/* RJ45 jack */}
      <rect x="96" y="26" width="30" height="34" rx="2" fill={CHIP_LIGHT} stroke={CHIP} strokeWidth="2" />
      <rect x="102" y="32" width="18" height="18" rx="1" fill="#0d0c16" />
      <rect x="108" y="50" width="6" height="6" fill="#0d0c16" />
      {/* link lights */}
      <circle cx="101" cy="57" r="2" fill="#22c58b" />
      <circle cx="121" cy="57" r="2" fill={GOLD} />
      {/* edge connector */}
      <rect x="34" y="66" width="40" height="10" fill={GOLD} />
      <rect x="80" y="66" width="24" height="10" fill={GOLD} />
      {/* bracket */}
      <rect x="12" y="14" width="8" height="60" rx="2" fill={METAL} stroke={CHIP} strokeWidth="2" />
    </svg>
  );
}

function Psu() {
  return (
    <svg {...frame} aria-label="A power supply: a metal box with a large cooling fan and a bundle of cables">
      <rect x="14" y="20" width="86" height="60" rx="4" fill={CHIP_LIGHT} stroke={CHIP} strokeWidth="3" />
      {/* fan */}
      <circle cx="57" cy="50" r="24" fill={CHIP} />
      <circle cx="57" cy="50" r="20" fill="none" stroke={METAL_DARK} strokeWidth="1.5" />
      {[0, 72, 144, 216, 288].map((a) => (
        <path
          key={a}
          d="M57 50 L57 32 A18 18 0 0 1 71 40 Z"
          fill={METAL_DARK}
          transform={`rotate(${a} 57 50)`}
          opacity="0.9"
        />
      ))}
      <circle cx="57" cy="50" r="6" fill={METAL} stroke={CHIP} strokeWidth="1.5" />
      {/* mains socket */}
      <rect x="18" y="24" width="14" height="12" rx="1.5" fill={CHIP} />
      {/* cables */}
      <path d="M100 34 C124 34 124 44 146 44" stroke={CHIP} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M100 46 C126 46 126 58 148 58" stroke={CHIP} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M100 58 C122 58 122 70 144 70" stroke={GOLD} strokeWidth="4" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function Gpu() {
  return (
    <svg {...frame} aria-label="A graphics card: a long board with two cooling fans, a bracket and display outputs">
      <rect x="22" y="18" width="126" height="52" rx="3" fill={CHIP} stroke={CHIP} strokeWidth="2" />
      {/* fans */}
      {[56, 108].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy="44" r="20" fill={CHIP_LIGHT} />
          {[0, 90, 180, 270].map((a) => (
            <path
              key={a}
              d={`M${cx} 44 L${cx} 28 A16 16 0 0 1 ${cx + 12} 34 Z`}
              fill={METAL_DARK}
              transform={`rotate(${a} ${cx} 44)`}
            />
          ))}
          <circle cx={cx} cy="44" r="5" fill={METAL} />
        </g>
      ))}
      {/* edge connector */}
      <rect x="40" y="70" width="46" height="9" fill={GOLD} />
      <rect x="92" y="70" width="22" height="9" fill={GOLD} />
      {/* bracket and display ports */}
      <rect x="12" y="12" width="10" height="64" rx="2" fill={METAL} stroke={CHIP} strokeWidth="2" />
      <rect x="14" y="24" width="6" height="10" rx="1" fill="#0d0c16" />
      <rect x="14" y="40" width="6" height="10" rx="1" fill="#0d0c16" />
    </svg>
  );
}

function Io() {
  return (
    <svg {...frame} aria-label="Input and output: a monitor with a keyboard and mouse in front of it">
      {/* monitor */}
      <rect x="34" y="10" width="92" height="54" rx="4" fill={CHIP} stroke={CHIP} strokeWidth="2" />
      <rect x="40" y="16" width="80" height="42" rx="2" fill="#7c4dff" />
      <path d="M46 50 L62 34 L74 44 L88 26 L114 50 Z" fill="#29b6f6" opacity="0.8" />
      <rect x="72" y="64" width="16" height="8" fill={METAL_DARK} />
      <rect x="60" y="72" width="40" height="5" rx="2" fill={METAL_DARK} />
      {/* keyboard */}
      <rect x="18" y="80" width="84" height="14" rx="3" fill={METAL} stroke={CHIP} strokeWidth="2" />
      {[24, 34, 44, 54, 64, 74, 84].map((x) => (
        <rect key={x} x={x} y="84" width="6" height="3" rx="1" fill={METAL_DARK} />
      ))}
      <rect x="36" y="89" width="44" height="3" rx="1.5" fill={METAL_DARK} />
      {/* mouse */}
      <rect x="112" y="80" width="20" height="14" rx="7" fill={METAL} stroke={CHIP} strokeWidth="2" />
      <path d="M122 80 V86" stroke={CHIP} strokeWidth="1.5" />
    </svg>
  );
}

export const COMPONENT_ART = {
  motherboard: Motherboard,
  cpu: Cpu,
  ram: Ram,
  storage: Storage,
  network: Network,
  psu: Psu,
  gpu: Gpu,
  io: Io,
};
